import * as THREE from 'three';
import renderScales from '@shared/render-scales';
import { loadMap as loadBoardSchema } from '../../../shared/board/map-loader';
import type { MapSchema } from '../../../shared/board/map-schema';
import type { BoardNode } from '../../../shared/board/types';
import { computeIslandSurfaceData } from '../board/islands';
import {
  getLightDirectionFromSunDirection,
  getSunDirectionFromSkyConfig,
} from '../atmosphere/daylight-sky';
import { createSkyQuakeHandler } from './sky-quake-handler';
import type { CameraJSON, DecorationsJSON, FXJSON, SkyforgeSchema, ThemeJSON } from './schema';
import {
  CameraJSONSchema,
  DecorationsJSONSchema,
  FXJSONSchema,
  SkyforgeSchemaSchema,
  ThemeJSONSchema,
} from './schema';
import type { LoadMapDeps } from './index';
import { DECORATION_MODEL_REGISTRY, MapAssemblyError } from './types';
import type { MapHandle, MapName, ThemeOverride } from './types';

export interface MapDataBundle {
  readonly name: MapName;
  readonly board: unknown;
  readonly decorations?: unknown;
  readonly theme: unknown;
  readonly fx?: unknown;
  readonly camera?: unknown;
}

function formatIssues(
  issues: ReadonlyArray<{ path: (string | number)[]; message: string }>,
): string {
  return issues.map((issue) => `${issue.path.join('.')}: ${issue.message}`).join(', ');
}

function buildTheme(
  themeJson: ThemeJSON,
  themeOverride: Partial<ThemeOverride> | undefined,
): ThemeOverride {
  const baseSunDirection = getSunDirectionFromSkyConfig(themeJson.sky);
  const baseTheme: ThemeOverride = {
    skyTopColor: themeJson.skyTopColor,
    skyBottomColor: themeJson.skyBottomColor,
    sky: themeJson.sky,
    clouds: themeJson.clouds,
    fog: { color: themeJson.fogColor, density: themeJson.fogDensity },
    lightingRig: {
      ...themeJson.lightingRig,
      keyDirection: getLightDirectionFromSunDirection(baseSunDirection),
    },
  };

  const sky = {
    ...baseTheme.sky,
    ...(themeOverride?.sky ?? {}),
  };
  const clouds = {
    ...baseTheme.clouds,
    ...(themeOverride?.clouds ?? {}),
  };
  const sunDirection = getSunDirectionFromSkyConfig(sky);
  const lightingRig = {
    ...baseTheme.lightingRig,
    ...(themeOverride?.lightingRig ?? {}),
    shadow: {
      ...(baseTheme.lightingRig.shadow ?? {}),
      ...(themeOverride?.lightingRig?.shadow ?? {}),
    },
    keyDirection:
      themeOverride?.lightingRig?.keyDirection ?? getLightDirectionFromSunDirection(sunDirection),
  };

  return {
    skyTopColor: themeOverride?.skyTopColor ?? baseTheme.skyTopColor,
    skyBottomColor: themeOverride?.skyBottomColor ?? baseTheme.skyBottomColor,
    sky,
    clouds,
    fog: {
      ...baseTheme.fog,
      ...(themeOverride?.fog ?? {}),
    },
    lightingRig,
  };
}

function parseBoardSchema(boardJson: unknown, label: string): SkyforgeSchema {
  const result = loadBoardSchema(boardJson as MapSchema);
  if (!result.ok) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `${label} board.json validation failed: ${result.errors.join(', ')}`,
    );
  }

  const board = boardJson as {
    name: string;
    version: number;
    bridges?: unknown;
    islandOverrides?: unknown;
    startingTreasureChestNodeId?: string;
    startingWeaponsCacheNodeId?: string | null;
    spawnNodeIds: string[];
    nodes: unknown[];
    spectatorArenaIslandId?: string;
    spectatorArenaSpawnNodeIds?: string[];
  };

  const bridgesParse = SkyforgeSchemaSchema.shape.bridges.safeParse(board.bridges ?? []);
  if (!bridgesParse.success) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `${label} bridges validation failed: ${formatIssues(bridgesParse.error.issues)}`,
    );
  }

  const islandOverridesParse = SkyforgeSchemaSchema.shape.islandOverrides.safeParse(
    board.islandOverrides,
  );
  if (!islandOverridesParse.success) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `${label} islandOverrides validation failed: ${formatIssues(
        islandOverridesParse.error.issues,
      )}`,
    );
  }

  const nodesParse = SkyforgeSchemaSchema.shape.nodes.safeParse(board.nodes);
  if (!nodesParse.success) {
    throw new MapAssemblyError(
      'invalid_board_json',
      `${label} nodes validation failed: ${formatIssues(nodesParse.error.issues)}`,
    );
  }

  const schema: SkyforgeSchema = {
    name: board.name,
    version: board.version,
    bridges: bridgesParse.data,
    islandOverrides: islandOverridesParse.data,
    nodes: nodesParse.data,
    startingTreasureChestNodeId: board.startingTreasureChestNodeId,
    startingWeaponsCacheNodeId: board.startingWeaponsCacheNodeId ?? null,
    spawnNodeIds: board.spawnNodeIds,
    spectatorArenaIslandId: board.spectatorArenaIslandId,
    spectatorArenaSpawnNodeIds: board.spectatorArenaSpawnNodeIds,
  };

  return schema;
}

function resolvedDecorationPosition(
  spec: DecorationsJSON['decorations'][number],
  islandSurfaceY: ReadonlyMap<string, { surfaceY: number }>,
): THREE.Vector3 {
  const position = new THREE.Vector3(spec.position.x, spec.position.y, spec.position.z);
  if (spec.lockedToIsland !== true || spec.islandId == null) return position;

  const surfaceY = islandSurfaceY.get(spec.islandId)?.surfaceY;
  if (surfaceY == null) return position;

  const legacyOffset = spec.position.y - surfaceY;
  position.y = surfaceY + (spec.surfaceOffsetY ?? legacyOffset);
  return position;
}

async function loadDecorations(
  decorationsJson: unknown,
  schema: SkyforgeSchema,
  deps: LoadMapDeps,
): Promise<{
  readonly decorationRoots: THREE.Object3D[];
  readonly lanternWrappers: THREE.Mesh[];
  readonly crystalMeshes: THREE.Object3D[];
  readonly fountainVectors: THREE.Vector3[];
  lanternFlickerGeometry: THREE.SphereGeometry | null;
}> {
  const decoParseResult = DecorationsJSONSchema.safeParse(
    decorationsJson ?? { decorations: [], crystals: [], fountainAnchors: [] },
  );
  if (!decoParseResult.success) {
    throw new MapAssemblyError(
      'invalid_decorations_json',
      `decorations.json validation failed: ${formatIssues(decoParseResult.error.issues)}`,
    );
  }
  const decoData = decoParseResult.data;
  const decorationRoots: THREE.Object3D[] = [];
  const lanternWrappers: THREE.Mesh[] = [];
  let lanternFlickerGeometry: THREE.SphereGeometry | null = null;
  const crystalMeshes: THREE.Object3D[] = [];
  const fountainVectors: THREE.Vector3[] = [];

  if (deps.loadDecorations === false) {
    return {
      decorationRoots,
      lanternWrappers,
      crystalMeshes,
      fountainVectors,
      lanternFlickerGeometry,
    };
  }

  if (!deps.gltfLoader) {
    return {
      decorationRoots,
      lanternWrappers,
      crystalMeshes,
      fountainVectors,
      lanternFlickerGeometry,
    };
  }

  const islandSurfaceY = computeIslandSurfaceData(
    schema.nodes as readonly BoardNode[],
    schema.islandOverrides,
    'chunk',
  );
  const byModelKey = new Map<string, typeof decoData.decorations>();
  for (const spec of decoData.decorations) {
    let group = byModelKey.get(spec.modelKey);
    if (!group) {
      group = [];
      byModelKey.set(spec.modelKey, group);
    }
    group.push(spec);
  }

  for (const [modelKey, specs] of byModelKey) {
    const url = DECORATION_MODEL_REGISTRY[modelKey as keyof typeof DECORATION_MODEL_REGISTRY];
    let gltfGroup: THREE.Group;
    try {
      gltfGroup = await deps.gltfLoader.load(url);
    } catch (err) {
      throw new MapAssemblyError(
        'glb_load_failed',
        `Failed to load GLB for modelKey "${modelKey}" (url="${url}"): ${
          err instanceof Error ? err.message : String(err)
        }`,
      );
    }

    let hasMesh = false;
    gltfGroup.traverse((child) => {
      if (child instanceof THREE.Mesh) hasMesh = true;
    });
    if (!hasMesh) continue;

    const quaternion = new THREE.Quaternion();
    const yAxis = new THREE.Vector3(0, 1, 0);

    for (const spec of specs) {
      const posVec = resolvedDecorationPosition(spec, islandSurfaceY);
      quaternion.setFromAxisAngle(yAxis, spec.rotationY);

      const root = new THREE.Group();
      root.name = `deco-${modelKey}`;
      root.position.copy(posVec);
      root.quaternion.copy(quaternion);
      root.scale.setScalar(spec.scale * renderScales.decoration.scaleMultiplier);
      root.add(gltfGroup.clone(true));

      deps.groups.decorations.add(root);
      decorationRoots.push(root);

      if (modelKey === 'lantern') {
        lanternFlickerGeometry ??= new THREE.SphereGeometry(0.01, 4, 2);
        const flickerMaterial = new THREE.MeshStandardMaterial({
          color: 0xffaa44,
          emissive: 0xffaa44,
          emissiveIntensity: 0.6,
        });
        const wrapper = new THREE.Mesh(lanternFlickerGeometry, flickerMaterial);
        wrapper.position.copy(posVec);
        lanternWrappers.push(wrapper);
      }
    }
  }

  const crystalMaterial = new THREE.MeshStandardMaterial({
    color: 0x88ccff,
    emissive: 0x4477aa,
    emissiveIntensity: 0.4,
  });
  for (const pos of decoData.crystals) {
    const crystalMesh = new THREE.Mesh(new THREE.IcosahedronGeometry(0.5), crystalMaterial);
    crystalMesh.position.set(pos.x, pos.y, pos.z);
    crystalMesh.name = 'deco-crystal';
    deps.groups.decorations.add(crystalMesh);
    crystalMeshes.push(crystalMesh);
  }

  for (const v of decoData.fountainAnchors) {
    fountainVectors.push(new THREE.Vector3(v.x, v.y, v.z));
  }

  return {
    decorationRoots,
    lanternWrappers,
    crystalMeshes,
    fountainVectors,
    lanternFlickerGeometry,
  };
}

export async function assembleMapFromData(
  bundle: MapDataBundle,
  deps: LoadMapDeps,
): Promise<MapHandle> {
  const label = bundle.name;
  const schema = parseBoardSchema(bundle.board, label);

  const themeParse = ThemeJSONSchema.safeParse(bundle.theme);
  if (!themeParse.success) {
    throw new MapAssemblyError(
      'invalid_theme_json',
      `${label} theme.json validation failed: ${formatIssues(themeParse.error.issues)}`,
    );
  }
  const theme = buildTheme(themeParse.data, deps.themeOverride);

  const fxParse = FXJSONSchema.safeParse(bundle.fx ?? {});
  const fx: FXJSON = fxParse.success ? fxParse.data : FXJSONSchema.parse({});

  const cameraParse = bundle.camera == null ? null : CameraJSONSchema.safeParse(bundle.camera);
  const camera: CameraJSON | null =
    cameraParse == null ? null : cameraParse.success ? cameraParse.data : null;

  const decorationState = await loadDecorations(bundle.decorations, schema, deps);
  let disposed = false;
  const skyQuakeHandler = createSkyQuakeHandler({
    scene: deps.scene,
    bridgeMeshes: [],
    registry: undefined,
  });

  return {
    name: bundle.name,
    schema,
    theme,
    fx,
    camera,
    getLanterns(): readonly THREE.Object3D[] {
      return Object.freeze([...decorationState.lanternWrappers]);
    },
    getCrystals(): readonly THREE.Object3D[] {
      return Object.freeze([...decorationState.crystalMeshes]);
    },
    getFountainAnchors(): readonly THREE.Vector3[] {
      return Object.freeze([...decorationState.fountainVectors]);
    },
    onSkyQuake: skyQuakeHandler.onSkyQuake.bind(skyQuakeHandler),
    dispose(): void {
      if (disposed) return;
      disposed = true;
      skyQuakeHandler.dispose();

      const geometries = new Set<THREE.BufferGeometry>();
      const materials = new Set<THREE.Material>();
      for (const root of decorationState.decorationRoots) {
        root.traverse((child) => {
          if (child instanceof THREE.Mesh) {
            geometries.add(child.geometry);
            if (Array.isArray(child.material)) {
              child.material.forEach((material) => materials.add(material));
            } else {
              materials.add(child.material as THREE.Material);
            }
          }
        });
        root.parent?.remove(root);
      }
      geometries.forEach((geometry) => geometry.dispose());
      materials.forEach((material) => material.dispose());
      decorationState.decorationRoots.length = 0;

      for (const crystal of decorationState.crystalMeshes) {
        if (crystal instanceof THREE.Mesh) {
          crystal.geometry.dispose();
          if (Array.isArray(crystal.material)) {
            crystal.material.forEach((material) => material.dispose());
          } else {
            (crystal.material as THREE.Material).dispose();
          }
        }
        crystal.parent?.remove(crystal);
      }
      decorationState.crystalMeshes.length = 0;

      for (const wrapper of decorationState.lanternWrappers) {
        wrapper.parent?.remove(wrapper);
        (wrapper.material as THREE.MeshStandardMaterial).dispose();
      }
      decorationState.lanternWrappers.length = 0;
      decorationState.lanternFlickerGeometry?.dispose();
      decorationState.lanternFlickerGeometry = null;
      decorationState.fountainVectors.length = 0;
    },
  };
}
